<template>
  <div class="flex h-full">
    <div class="flex-1 overflow-auto p-4 flex justify-between flex-col">
      <!-- List Audit Logs -->
      <div>
        <div>
          <p
            v-if="!uiFlags.fetchingList && !records.length"
            class="flex h-full items-center flex-col justify-center"
          >
            {{ $t('AUDIT_LOGS.LIST.404') }}
          </p>
          <woot-loading-state
            v-if="uiFlags.fetchingList"
            :message="$t('AUDIT_LOGS.LOADING')"
          />

          <table
            v-if="!uiFlags.fetchingList && records.length"
            class="woot-table w-full"
          >
            <colgroup>
              <col class="w-[60%]" />
              <col />
              <col />
            </colgroup>
            <thead>
              <!-- Header -->
              <th
                v-for="thHeader in $t('AUDIT_LOGS.LIST.TABLE_HEADER')"
                :key="thHeader"
              >
                {{ thHeader }}
              </th>
            </thead>
            <tbody>
              <tr v-for="auditLogItem in records" :key="auditLogItem.id">
                <td class="whitespace-nowrap break-all">
                  {{ generateLogText(auditLogItem) }}
                </td>
                <td class="whitespace-nowrap break-all">
                  {{
                    messageTimestamp(
                      auditLogItem.created_at,
                      'MMM dd, yyyy hh:mm a'
                    )
                  }}
                </td>
                <td class="w-[8.75rem]">
                  {{ auditLogItem.remote_address }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <table-footer
        :current-page="Number(meta.currentPage)"
        :total-count="meta.totalEntries"
        :page-size="meta.perPage"
        class="dark:bg-slate-900"
        @page-change="onPageChange"
      />
    </div>
    <woot-modal
      :show.sync="showFiltersModal"
      :on-close="closeAdvanceFiltersModal"
      size="medium"
    >
      <audit-advanced-filters
        v-if="showFiltersModal"
        :on-close="closeAdvanceFiltersModal"
        :initial-filter-types="auditLogFilterItems"
        :initial-applied-filters="appliedFilter"
        @applyFilter="onApplyFilter"
        @clearFilters="clearFilters"
      />
    </woot-modal>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import AuditAdvancedFilters from './AuditAdvancedFilters.vue';
import { filterItems } from './auditlogsFilterItems';
import timeMixin from 'dashboard/mixins/time';
import alertMixin from 'shared/mixins/alertMixin';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';

const DEFAULT_PAGE = 1;

export default {
  components: {
    TableFooter,
    AuditAdvancedFilters,
  },
  mixins: [alertMixin, timeMixin],
  data() {
    return {
      loading: {},
      auditLogsAPI: {
        message: '',
      },
      showFiltersModal: false,
      appliedFilter: [],
      lastAppliedFilter: [],
      segmentsId: null,
      auditLogFilterItems: filterItems.map(filter => ({
        ...filter,
        attributeName: this.$t(
          `AUDIT_LOGS_FILTER.ATTRIBUTES.${filter.attributeI18nKey}`
        ),
      })),
    };
  },
  computed: {
    ...mapGetters({
      records: 'auditlogs/getAuditLogs',
      uiFlags: 'auditlogs/getUIFlags',
      meta: 'auditlogs/getMeta',
      agentList: 'agents/getAgents',
      getAppliedAuditlogsFilters: 'auditlogs/getAppliedAuditlogsFilters',
      getSearchQuery: 'auditlogs/getSearchQuery',
      getExportType: 'auditlogs/getCurrentExportType',
    }),
    hasAppliedFilters() {
      return this.getAppliedAuditlogsFilters.length;
    },
  },
  mounted() {
    // Fetch API Call
    this.$store.dispatch('agents/get');

    // Fetch audit logs based on current export type (preserve user's last action)
    const page = this.$route.query.page ?? DEFAULT_PAGE;
    switch (this.getExportType) {
      case 'search':
        this.submitSearch(page);
        break;
      case 'filter':
        this.fetchFilteredAuditLogs(page);
        break;
      default:
        this.fetchAuditLogs();
        break;
    }

    // Listen for filter modal open event from AuditFilter component
    this.$root.$on('openAuditFiltersModal', this.openAdvanceFiltersModal);
    this.$root.$on('onSubmitSearch', this.submitSearch);
  },
  beforeDestroy() {
    // Clean up event listener
    this.$root.$off('openAuditFiltersModal', this.openAdvanceFiltersModal);
    this.$root.$off('onSubmitSearch', this.submitSearch);
  },
  methods: {
    fetchAuditLogs(page = 1) {
      this.$store.dispatch('auditlogs/fetch', { page }).catch(error => {
        const errorMessage =
          error?.message || this.$t('AUDIT_LOGS.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      });
    },
    generateLogText(auditLogItem) {
      const translationPayload = generateTranslationPayload(
        auditLogItem,
        this.agentList
      );
      const translationKey = generateLogActionKey(auditLogItem);

      if (auditLogItem.comment === 'role:update') {
        translationPayload.role_name = auditLogItem.audited_changes?.role_name || '';
      }

      return this.$t(translationKey, translationPayload);
    },
    onPageChange(page) {
      this.updatePageParam(page);
      try {
        // Determine which API to call based on export type
        switch (this.getExportType) {
          case 'search':
            this.submitSearch(page);
            break;
          case 'filter':
            this.fetchFilteredAuditLogs(page);
            break;
          default:
            this.fetchAuditLogs(page);
            break;
        }
      } catch (error) {
        const errorMessage =
          error?.message || this.$t('AUDIT_LOGS.API.ERROR_MESSAGE');
        this.showAlert(errorMessage);
      }
    },
    submitSearch(page) {
      // Clear any applied filters when searching
      if (this.hasAppliedFilters) {
        this.appliedFilter = [];
        this.$store.dispatch('auditlogs/clearAuditLogsFilters');
        this.$store.commit('auditlogs/SET_AUDIT_LOGS_FILTER_QUERY', null);
      }

      if (this.getSearchQuery) {
        this.$store.commit('auditlogs/SET_AUDIT_LOGS_EXPORT_TYPE', 'search');
        this.$store.dispatch('auditlogs/search', {
          search: this.getSearchQuery,
          page: page || 1,
        });
      } else {
        this.fetchAuditLogs(page);
      }
    },
    openAdvanceFiltersModal() {
      this.showFiltersModal = true;
    },
    closeAdvanceFiltersModal() {
      this.appliedFilter = this.lastAppliedFilter;
      this.$store.commit(
        'auditlogs/SET_AUDIT_LOGS_FILTER_QUERY',
        this.appliedFilter
      );
      this.showFiltersModal = false;
    },
    onApplyFilter(payload) {
      this.lastAppliedFilter = payload;
      this.segmentsQuery = filterQueryGenerator(payload);
      this.$store.commit(
        'auditlogs/SET_AUDIT_LOGS_FILTER_QUERY',
        this.segmentsQuery
      );
      this.$store.commit('auditlogs/SET_AUDIT_LOGS_EXPORT_TYPE', 'filter');
      this.fetchFilteredAuditLogs(DEFAULT_PAGE);
      this.showFiltersModal = false;
    },
    clearFilters() {
      this.appliedFilter = [];
      this.lastAppliedFilter = [];
      this.$store.dispatch('auditlogs/clearAuditLogsFilters');
      this.$store.commit('auditlogs/SET_AUDIT_LOGS_FILTER_QUERY', null);
      this.$store.commit('auditlogs/SET_AUDIT_LOGS_EXPORT_TYPE', null);
      this.showFiltersModal = false;
      this.fetchAuditLogs();
    },
    updatePageParam(page) {
      window.history.pushState({}, null, `${this.$route.path}?page=${page}`);
    },
    // eslint-disable-next-line no-unused-vars
    fetchSavedFilteredAuditLogs(payload, page) {},
    fetchFilteredAuditLogs(page) {
      if (this.hasAppliedFilters) {
        this.$store.dispatch('auditlogs/filter', {
          queryPayload: this.segmentsQuery,
          page,
        });
      } else {
        this.$store.dispatch('auditlogs/fetch', { page });
      }
    },
  },
};
</script>
